import { computed, ref, type Ref } from 'vue';

import { AppLocale } from '../../i18n/types';
import { CookieConsentDecision } from '../cookie-consent/types';
import { ThemePreference } from '../theme/types';
import type { AppStorage, AppStorageController } from './types';

const APP_STORAGE_KEY: string = 'family-space-chat.app-storage';
const LEGACY_APP_STORAGE_KEY: string = 'family-chat.app-storage';
const APP_STORAGE_VERSION: number = 3;

type AppStorageBase = Omit<AppStorage, 'cookieConsent' | 'version'> & {
  cookieConsent?: unknown;
  version: number;
};

const createDefaultStorage = (): AppStorage => ({
  cookieConsent: {
    notRequired: null,
  },
  interface: {
    locale: AppLocale.RU,
    panels: {
      chatUserProfileOpened: false,
      widths: {},
    },
    theme: ThemePreference.SYSTEM,
  },
  pwa: {
    installBannerDismissed: false,
  },
  version: APP_STORAGE_VERSION,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isThemePreference = (value: unknown): value is ThemePreference =>
  typeof value === 'string' && Object.values(ThemePreference).includes(value as ThemePreference);

const isAppLocale = (value: unknown): value is AppLocale =>
  typeof value === 'string' && Object.values(AppLocale).includes(value as AppLocale);

const isCookieConsentDecision = (value: unknown): value is CookieConsentDecision =>
  typeof value === 'string' &&
  Object.values(CookieConsentDecision).includes(value as CookieConsentDecision);

const isPanelWidths = (value: unknown): value is Record<string, number> => {
  if (!isRecord(value)) {
    return false;
  }

  return Object.values(value).every(
    (width: unknown): boolean => typeof width === 'number' && Number.isFinite(width),
  );
};

const isAppStorageBase = (value: unknown): value is AppStorageBase => {
  if (!isRecord(value) || !isRecord(value.interface)) {
    return false;
  }

  const interfaceStorage: Record<string, unknown> = value.interface;
  const pwaStorage: unknown = value.pwa;

  if (!isRecord(pwaStorage) || !isRecord(interfaceStorage.panels)) {
    return false;
  }

  const panels: Record<string, unknown> = interfaceStorage.panels;

  return (
    isThemePreference(interfaceStorage.theme) &&
    isAppLocale(interfaceStorage.locale) &&
    typeof panels.chatUserProfileOpened === 'boolean' &&
    isPanelWidths(panels.widths) &&
    typeof pwaStorage.installBannerDismissed === 'boolean'
  );
};

const isAppStorage = (value: unknown): value is AppStorage => {
  if (
    !isAppStorageBase(value) ||
    value.version !== APP_STORAGE_VERSION ||
    !isRecord(value.cookieConsent)
  ) {
    return false;
  }

  const cookieConsent: Record<string, unknown> = value.cookieConsent;

  return cookieConsent.notRequired === null || isCookieConsentDecision(cookieConsent.notRequired);
};

const migrateStorage = (value: unknown): AppStorage | null => {
  if (isAppStorage(value)) {
    return value;
  }

  if (!isAppStorageBase(value) || (value.version !== 1 && value.version !== 2)) {
    return null;
  }

  const cookieConsent: unknown = value.cookieConsent;
  const notRequired: unknown = isRecord(cookieConsent) ? cookieConsent.notRequired : null;

  return {
    interface: value.interface,
    pwa: value.pwa,
    cookieConsent: {
      notRequired:
        notRequired === null || isCookieConsentDecision(notRequired) ? notRequired : null,
    },
    version: APP_STORAGE_VERSION,
  };
};

const persistMigratedStorage = (storage: AppStorage): void => {
  try {
    window.localStorage.setItem(APP_STORAGE_KEY, JSON.stringify(storage));
  } catch {
    // Keep using the in-memory snapshot if storage is unavailable.
    return;
  }

  try {
    window.localStorage.removeItem(LEGACY_APP_STORAGE_KEY);
  } catch {
    // The migrated copy is already available under the current key.
  }
};

const loadStorage = (): AppStorage => {
  try {
    const rawStorage: string | null = window.localStorage.getItem(APP_STORAGE_KEY);

    if (rawStorage !== null) {
      const parsedStorage: unknown = JSON.parse(rawStorage);
      const storage: AppStorage | null = migrateStorage(parsedStorage);

      if (storage !== null) {
        if (!isRecord(parsedStorage) || parsedStorage.version !== APP_STORAGE_VERSION) {
          persistMigratedStorage(storage);
        }

        return storage;
      }
    }

    const legacyStorage: string | null = window.localStorage.getItem(LEGACY_APP_STORAGE_KEY);
    if (legacyStorage === null) {
      return createDefaultStorage();
    }

    const migratedStorage: AppStorage | null = migrateStorage(JSON.parse(legacyStorage));
    if (migratedStorage === null) {
      return createDefaultStorage();
    }

    persistMigratedStorage(migratedStorage);

    return migratedStorage;
  } catch {
    return createDefaultStorage();
  }
};

const createAppStorageController = (): AppStorageController => {
  const storage: Ref<AppStorage> = ref<AppStorage>(createDefaultStorage());
  let isLoaded: boolean = false;

  const ensureLoaded = (): void => {
    if (isLoaded) {
      return;
    }

    storage.value = loadStorage();
    isLoaded = true;
  };

  const persist = (): void => {
    ensureLoaded();

    try {
      window.localStorage.setItem(APP_STORAGE_KEY, JSON.stringify(storage.value));
    } catch {
      return;
    }
  };

  const themePreference = computed<ThemePreference>({
    get: (): ThemePreference => {
      ensureLoaded();
      return storage.value.interface.theme;
    },
    set: (preference: ThemePreference): void => {
      ensureLoaded();
      storage.value.interface.theme = preference;
      persist();
    },
  });
  const locale = computed<AppLocale>({
    get: (): AppLocale => {
      ensureLoaded();
      return storage.value.interface.locale;
    },
    set: (newLocale: AppLocale): void => {
      ensureLoaded();
      storage.value.interface.locale = newLocale;
      persist();
    },
  });
  const chatUserProfileOpened = computed<boolean>({
    get: (): boolean => {
      ensureLoaded();
      return storage.value.interface.panels.chatUserProfileOpened;
    },
    set: (opened: boolean): void => {
      ensureLoaded();
      storage.value.interface.panels.chatUserProfileOpened = opened;
      persist();
    },
  });
  const pwaInstallBannerDismissed = computed<boolean>({
    get: (): boolean => {
      ensureLoaded();
      return storage.value.pwa.installBannerDismissed;
    },
    set: (dismissed: boolean): void => {
      ensureLoaded();
      storage.value.pwa.installBannerDismissed = dismissed;
      persist();
    },
  });
  const notRequiredCookieConsent = computed<CookieConsentDecision | null>({
    get: (): CookieConsentDecision | null => {
      ensureLoaded();
      return storage.value.cookieConsent.notRequired;
    },
    set: (decision: CookieConsentDecision | null): void => {
      ensureLoaded();
      storage.value.cookieConsent.notRequired = decision;
      persist();
    },
  });
  const getPanelWidth = (panelKey: string): number | null => {
    ensureLoaded();

    return storage.value.interface.panels.widths[panelKey] ?? null;
  };

  const setThemePreference = (preference: ThemePreference): void => {
    themePreference.value = preference;
  };

  const setPanelWidth = (panelKey: string, width: number): void => {
    ensureLoaded();
    storage.value.interface.panels.widths[panelKey] = width;
    persist();
  };

  const setChatUserProfileOpened = (opened: boolean): void => {
    chatUserProfileOpened.value = opened;
  };

  const setNotRequiredCookieConsent = (decision: CookieConsentDecision): void => {
    notRequiredCookieConsent.value = decision;
  };

  const setPwaInstallBannerDismissed = (dismissed: boolean): void => {
    pwaInstallBannerDismissed.value = dismissed;
  };

  const hydrate = (snapshot: AppStorage): void => {
    if (!isAppStorage(snapshot)) {
      return;
    }

    storage.value = snapshot;
    isLoaded = true;
    persist();
  };

  return {
    chatUserProfileOpened,
    getPanelWidth,
    hydrate,
    locale,
    notRequiredCookieConsent,
    pwaInstallBannerDismissed,
    setChatUserProfileOpened,
    setNotRequiredCookieConsent,
    setPanelWidth,
    setPwaInstallBannerDismissed,
    setThemePreference,
    themePreference,
  };
};

const appStorageController: AppStorageController = createAppStorageController();

export { APP_STORAGE_KEY, appStorageController, createAppStorageController };
